import { Form } from 'antd';
import { useCallback, useMemo } from 'react';

import { PublicationSelectionMode } from '@app/components/Modals/BotDetailsModal/types';
import {
  getInstagramPublicationsError,
  getInstagramPublicationsIntegrationId,
} from '@app/components/Modals/BotDetailsModal/utils/instagramPublications';
import { INSTAGRAM } from '@app/constants/socialIntegrationsTypes';

import type { FormRule } from 'antd';

interface UseInstagramPublicationsFieldProps {
  socialIntegrationType: string | undefined;
}

export const PUBLICATION_MODE_TAB_KEYS: Record<
  PublicationSelectionMode,
  string
> = {
  [PublicationSelectionMode.All]: 'all',
  [PublicationSelectionMode.Selected]: 'selected',
};

export const useInstagramPublicationsField = ({
  socialIntegrationType,
}: UseInstagramPublicationsFieldProps) => {
  const form = Form.useFormInstance();
  const socialIntegrationId: unknown = Form.useWatch(
    'socialIntegrationId',
    form
  );
  const publicationSelectionMode: PublicationSelectionMode | undefined =
    Form.useWatch('publicationSelectionMode', form);

  const integrationId = getInstagramPublicationsIntegrationId(
    socialIntegrationType,
    socialIntegrationId
  );
  // Канал Instagram выбран, а конкретная интеграция — нет
  const isIntegrationMissing =
    socialIntegrationType === INSTAGRAM && integrationId === null;
  const isSelectedMode =
    publicationSelectionMode === PublicationSelectionMode.Selected;
  const activeTabKey =
    PUBLICATION_MODE_TAB_KEYS[
      isSelectedMode
        ? PublicationSelectionMode.Selected
        : PublicationSelectionMode.All
    ];

  const modeTabChangeHandler = useCallback(
    (tabKey: string) => {
      const mode =
        tabKey === PUBLICATION_MODE_TAB_KEYS[PublicationSelectionMode.Selected]
          ? PublicationSelectionMode.Selected
          : PublicationSelectionMode.All;

      form.setFieldValue('publicationSelectionMode', mode);

      // В режиме «все» список не проверяется — убираем старую ошибку
      if (mode === PublicationSelectionMode.All) {
        form.setFields([{ name: 'instagramPublicationIds', errors: [] }]);
      }
    },
    [form]
  );

  const publicationIdsRules = useMemo<FormRule[]>(
    () => [
      ({ getFieldValue }) => ({
        validator: (_rule, value: string[] | undefined) => {
          const error = getInstagramPublicationsError(
            getFieldValue('publicationSelectionMode'),
            value
          );

          return error ? Promise.reject(new Error(error)) : Promise.resolve();
        },
      }),
    ],
    []
  );

  return {
    integrationId,
    isIntegrationMissing,
    isSelectedMode,
    activeTabKey,
    modeTabChangeHandler,
    publicationIdsRules,
  };
};
